import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import { useWelcomeGiftVerifyMutation } from "@/hooks/useWelcomeGiftVerifyMutation";
import { Spinner } from "@/components/Spinner";
import type { WelcomeGiftVerifyRequest } from "@da-web/common";
import { useWelcomeGiftStore } from "@/hooks/welcomeGiftStore";
import { useWelcomeGiftRegistrationMutation } from "@/hooks/useWelcomeGiftRegistrationMutation";
import { useBrandConfigStore } from "@/hooks/brandConfigStore";
import { trackEvent } from "@/lib/analytics";

const RESEND_COOLDOWN = 5; // seconds

export const Route = createFileRoute("/(welcome-gift)/welcome-gift/otp")({
    component: WelcomeGiftOtp,
});

function WelcomeGiftOtp() {
    const navigate = useNavigate()
    const [otp, setOtp] = useState<string[]>(new Array(4).fill(""));
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [otpResendMessage, setOtpResendMessage] = useState<string>("");
    const { payload: otpPayload, setPayload } = useWelcomeGiftStore();
    const { mutate: resendOtp } = useWelcomeGiftRegistrationMutation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [timeLeft, setTimeLeft] = useState<number>(0);

    const { config } = useBrandConfigStore()

    if (!config) return null;

    const resendCooldown = config.resendCooldownSeconds ?? RESEND_COOLDOWN;

    const { mutate } = useWelcomeGiftVerifyMutation();

    const inputRefs = Array.from({ length: 4 }, () => useRef<HTMLInputElement>(null));

    const isButtonDisabled = isLoading || otp.join("").length !== 4

    useEffect(() => {
        if (!otpPayload.phone) {
            navigate({ to: "/welcome-gift", replace: true })
            return
        }
        const calculateTimeLeft = () => {
            const storedExpiry = localStorage.getItem("otp_expiry");
            if (!storedExpiry) return 0;
            const expiryTime = parseInt(storedExpiry, 10);
            const remaining = Math.max(0, Math.ceil((expiryTime - Date.now()) / 1000));
            if (remaining === 0) {
                localStorage.removeItem("otp_expiry");
            }
            return remaining;
        };

        const existingExpiry = localStorage.getItem("otp_expiry");
        const maxExpiryTime = Date.now() + resendCooldown * 1000;
        if (!existingExpiry || parseInt(existingExpiry, 10) > maxExpiryTime) {
            localStorage.setItem("otp_expiry", maxExpiryTime.toString());
        }

        setTimeLeft(calculateTimeLeft());

        const timer = setInterval(() => {
            const remaining = calculateTimeLeft();
            setTimeLeft(remaining);
        }, 1000);

        return () => clearInterval(timer);
    }, [resendCooldown]);

    const handleResend = () => {
        const expiryTime = Date.now() + resendCooldown * 1000;
        localStorage.setItem("otp_expiry", expiryTime.toString());
        setTimeLeft(resendCooldown);
        resendOtp(otpPayload, {
            onSuccess: () => {
                setOtpResendMessage(`*code has been resent to \n ${otpPayload.phone}`)
            },
            onError: () => {
                setErrorMessage("Failed to resend otp")
            }
        })
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { value } = e.target;
        if (/[^0-9]/.test(value)) return;

        if (otpResendMessage) setOtpResendMessage("")

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 3) {
            inputRefs[index + 1].current?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs[index - 1].current?.focus();
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true)

        const payload: WelcomeGiftVerifyRequest = {
            otp: otp.join(""),
            name: otpPayload.name,
            brandID: config.brandID || otpPayload.brandID,
        };

        mutate(payload, {
            onSuccess: async (data) => {
                await trackEvent(
                    "welcome_gift_registered",
                    {
                        phoneNumber: otpPayload.phone,
                        email: otpPayload.email,
                    },
                    data?.customerId
                );
                setPayload({
                    phone: "",
                    name: "",
                    firstName: "",
                    lastName: "",
                    email: "",
                    brandID: ""
                })
                await navigate({ to: "/welcome-gift/result", replace: true })
            },
            onError: (data: any) => {
                const errorCode: string = data?.error?.code ?? ""
                if (errorCode.includes("duplicate")) {
                    navigate({ to: "/welcome-gift/error", replace: true })
                } else {
                    setErrorMessage("The code you entered is incorrect.\nPlease try again")
                }
                setIsLoading(false)
            },
        });
    };


    return (
        <div className="flex flex-col w-full h-full justify-center" style={{ fontFamily: config.fonts.mainFont }}>
            <div
                className="flex flex-col items-center text-white pb-12 h-[95%] sm:h-[80%] px-6"
                style={{ backgroundColor: config.bgColors.mainColor }}
            >
                <img src={config.logoUrl} alt="Logo" style={config.logoStyle} />
                <p className={`text-3xl font-bold text-center`} style={{ fontFamily: config.fonts.titleFont }}>
                    CLAIM YOUR
                </p>
                <p className={`text-3xl font-bold text-center`} style={{ fontFamily: config.fonts.titleFont }}>
                    WELCOME GIFT
                </p>
                <form onSubmit={handleSubmit} className="space-y-4 mt-8">
                    <div className="flex justify-center space-x-2">
                        {otp.map((digit, index) => (
                            <div className={errorMessage ? `border border-gray-100 rounded-md` : ``}>
                                <input
                                    key={index}
                                    id={`otp-${index}`}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleChange(e, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    ref={inputRefs[index]}
                                    className={`block w-12 m-1 h-12 text-center bg-gray-100 rounded-md sm:text-lg focus:outline-none ${errorMessage ? `text-[${config.bgColors.mainColor}]` : `text-gray-900`}`}
                                    style={errorMessage ? { color: config.bgColors.mainColor } : {}}
                                    required
                                />
                            </div>
                        ))}
                    </div>
                    {!!errorMessage && (
                        <div style={{ whiteSpace: "pre-line" }} className="text-center text-sm">
                            {errorMessage}
                        </div>
                    )}
                    <div className="flex flex-col items-center mt-4 w-full space-y-4">
                        <button
                            type="submit"
                            className={`w-[50%] px-4 py-2 mt-6 font-bold rounded-md flex justify-center items-center`}
                            style={{
                                backgroundColor: isButtonDisabled ? "#9ca3af" : config.buttonColors.bgColor,
                                color: config.buttonColors.fontColor,
                            }}
                            disabled={isLoading || otp.join("").length !== 4}
                        >
                            {isLoading ? (
                                <Spinner className="w-5 h-5 text-sm text-white" />
                            ) : (
                                errorMessage ? "TRY AGAIN" : "SUBMIT"
                            )}
                        </button>
                        <div className="text-sm">
                            Didn't get the code?{" "}
                            {timeLeft > 0 ? (
                                <span className="underline">
                                    Resend code in {timeLeft}s
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    className="font-bold underline"
                                >
                                    Resend code
                                </button>
                            )}
                        </div>
                    </div>
                </form>
                <div className="mt-auto">
                    <p className="text-xl font-bold text-center">{otpResendMessage}</p>
                </div>
            </div>
        </div>
    );
}
